import { Box } from "@mui/material";
import {
  Family,
  GetFamilyDetailsResponse_FamilyDashboardAlerts,
} from "protogen/advisors_service_pb";
import DashboardAlert, {
  LinkAction,
  MessageAction,
  TaskAction,
} from "./dashboard/DashboardAlert";
import FamilyDetails from "./dashboard/FamilyDetails";
import FamilyHealth from "./dashboard/FamilyHealth";
import { PlanUtilization } from "protogen/common_pb";
import FlexBox from "components/common/FlexBox";
import FamilyTaskSuggestions from "./dashboard/FamilyTaskSuggestions";
import { PanelProps } from "components/details-page/TabbedDetailsPanel";
import useLocalStorageState from "components/hooks/useLocalStorageState";
import { OctagonAlert, SquareCheckBig } from "lucide-react";
import FamilyMagicDocs from "./dashboard/FamilyMagicDocs";
import UpcomingVideoCalls from "components/common/UpcomingVideoCalls";
import {
  createTaskInspirationLink,
  TASK_INSPO_PERSONALIZED_TASKS,
} from "./dashboard/constants";
import {
  TaskInspiration,
  TaskInspirationCategory,
  TaskWishlistEntry,
} from "protogen/tasks_pb";
import { AgentPrompt } from "protogen/agents_service_pb";

interface Props extends PanelProps {
  title: string;
  family: Family;
  planUtilization: PlanUtilization;
  alerts: GetFamilyDetailsResponse_FamilyDashboardAlerts;
  taskInspirations?: TaskInspiration[];
  taskInspirationCategories?: TaskInspirationCategory[];
  magicDocs?: AgentPrompt[];
  taskWishlistEntries?: TaskWishlistEntry[];
}

const StaleCommsAlert = ({
  daysSinceLastComms,
  onClose,
}: {
  daysSinceLastComms: number;
  onClose: () => void;
}) => (
  <DashboardAlert
    title={`No communication in ${daysSinceLastComms} days`}
    description={
      <FlexBox direction="column">
        Tip: If a client is being quiet, offering very specific tasks can help
        them be more decisive. For example: “I just booked a mobile car detailer
        for another family, would you be interested in the same?”
      </FlexBox>
    }
    icon={<OctagonAlert color="#C14743" size={20} />}
    onClose={onClose}
    action={
      <MessageAction message="Hey! It’s been a while, I have ideas of things I can help with if you want to chat! What time works for you?" />
    }
  />
);

const OverUtilizationAlert = ({ onClose }: { onClose: () => void }) => (
  <DashboardAlert
    title="Plan overutilization"
    description="This family has gone over their usable hours for the month. Suggest a plan upgrade."
    onClose={onClose}
    action={
      <MessageAction
        message="Hi there! It seems we've gone over your plan's hours for the month.
        Should we consider upgrading your plan?"
      />
    }
  />
);

const UnderUtilizationAlert = ({
  percentUsed,
  onClose,
  familyRef,
}: {
  percentUsed: number;
  onClose: () => void;
  familyRef: string;
}) => (
  <DashboardAlert
    title="Plan underutilization"
    description={
      (percentUsed > 10
        ? `This family's plan ends soon and they've only used ${percentUsed}% of it.`
        : "This family's plan ends soon and hasn't been actively used.") +
      " Here are some ways to engage them before it ends."
    }
    onClose={onClose}
    action={
      <>
        <MessageAction message="Hi there! What can I help out with this week?" />
        <TaskAction
          message="Personalized task ideas"
          url={createTaskInspirationLink({
            ...TASK_INSPO_PERSONALIZED_TASKS,
            familyRef,
          })}
        />
      </>
    }
  />
);

const FirstTaskAlert = ({ onClose }: { onClose: () => void }) => (
  <DashboardAlert
    title="How fast can you solve your first task?"
    description="One of the best ways to create immediate “magic” is to solve the first task quickly, within hours or the same day! Even if your tasks are project based and complicated, try to hop on one element of it immediately and give an update as soon as you can."
    icon={<SquareCheckBig color="#198282" size={20} />}
    onClose={onClose}
    action={<LinkAction message="Go to tasks" url={"#tab-tasks"} />}
  />
);

export default ({
  family,
  planUtilization,
  alerts,
  taskInspirations,
  taskInspirationCategories,
  magicDocs,
  taskWishlistEntries,
}: Props) => {
  const [alertDismissed, setAlertDismissed] = useLocalStorageState<{
    overUtilization?: number;
    underUtilization?: number;
    nonCommunicating?: number;
    firstTask?: number;
  }>(`family-alerts-dismissed-${family.ref}`, {});

  const checkAlert = (alert: keyof typeof alertDismissed) => {
    const DISMISS_INTERVAL = 1000 * 60 * 60 * 24 * 7;
    if ((alertDismissed[alert] ?? 0) > Date.now() - DISMISS_INTERVAL) {
      return true;
    }
    return false;
  };

  const dismissAlert = (alert: keyof typeof alertDismissed) => {
    setAlertDismissed((prev) => ({ ...prev, [alert]: Date.now() }));
  };

  /* Communication Alerts */
  const daysSinceLastComms = Math.floor(
    (Date.now() / 1000 - Number(alerts.lastCommunicationSec)) / (24 * 60 * 60),
  );
  const isNonCommunicating =
    !checkAlert("nonCommunicating") &&
    alerts.lastCommunicationSec &&
    daysSinceLastComms > 7;

  /* Utilization Alerts */
  const percentUsed = Math.round(
    (planUtilization.periodHoursUtilized /
      (planUtilization.planHoursAllocated || 1)) *
      100,
  );
  const isUnderUtilization =
    !checkAlert("underUtilization") && alerts.underUtilization;
  // Show the overage alert if the family is over by over an hour.
  const isOverUtilization =
    !checkAlert("overUtilization") && alerts.overUtilization;

  /* Other */
  const hasNoTaskProgress =
    !checkAlert("firstTask") && Number(alerts.lastCompletedTaskSec) === 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* Top Info Modules */}
      {family.status === 2 && (
        <>
          {isNonCommunicating && (
            <StaleCommsAlert
              daysSinceLastComms={daysSinceLastComms}
              onClose={() => dismissAlert("nonCommunicating")}
            />
          )}
          {isOverUtilization && (
            <OverUtilizationAlert
              onClose={() => dismissAlert("overUtilization")}
            />
          )}
          {isUnderUtilization && (
            <UnderUtilizationAlert
              percentUsed={percentUsed}
              onClose={() => dismissAlert("underUtilization")}
              familyRef={family.ref}
            />
          )}

          {hasNoTaskProgress && (
            <FirstTaskAlert onClose={() => dismissAlert("firstTask")} />
          )}
        </>
      )}
      {/* Upcoming Video Calls */}
      <UpcomingVideoCalls family={family} />
      {/* Bottom Section: Family Details & Health */}
      <FlexBox stackedMobile gap="16px" alignItems="stretch">
        <FamilyHealth
          family={family}
          planUtilization={planUtilization}
          flex={3}
        />
        <FamilyDetails family={family} flex={2} />
      </FlexBox>
      <FlexBox stackedMobile gap="16px" alignItems="stretch">
        <FamilyTaskSuggestions
          family={family}
          flex={3}
          taskInspirations={taskInspirations}
          taskInspirationCategories={taskInspirationCategories}
          taskWishlistEntries={taskWishlistEntries}
        />
        <FamilyMagicDocs
          flex={2}
          familyRef={family.ref}
          magicDocs={magicDocs}
        />
      </FlexBox>
    </Box>
  );
};
